import React from "react";
import { useParams, Link as RouterLink } from "react-router-dom";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "../../components/ui/breadcrumb";
import { Helmet } from "react-helmet";
import Navbar from "../../_components/Navbar/Navbar";
import Footer from "../../_components/Footer";
import { glossaryData, GlossaryTerm } from "./constants";
import { allowedArtisans } from "../hire-a-pro/constants";
import { FaArrowLeft } from "react-icons/fa";
import { PromotionalText } from "./components/promotional-text";
import { useCategories } from "hooks/useCategories";

interface TermDetailParams {
  category: string;
  letter: string;
  termSlug: string;
}

export const TermDetail: React.FC = () => {
  const { category, letter, termSlug } = useParams<TermDetailParams>();

  if (!category || !letter || !termSlug) {
    return <div>Invalid parameters</div>;
  }

  const {
    categories,
    loading: categoriesLoading,
    error: categoriesError,
  } = useCategories();

  const categoryId = categories?.find(
    (c) =>
      c.name.toLowerCase() === category.toLowerCase() ||
      c.name.toLowerCase() === category.toLowerCase().replace(/-/g, " ") ||
      c.name.toLowerCase() === category.toLowerCase().replace(/-/g, "")
  )?.id;

  // Check if artisan exists in glossary data
  if (!glossaryData[category]) {
    return <div>Category glossary not found</div>;
  }

  // Find the term by converting slug back to term name
  const term = glossaryData[category].terms.find((t: GlossaryTerm) => {
    const slug = t.term
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^\w-]/g, "");
    return slug === termSlug.toLowerCase();
  });

  if (!term) {
    return <div>Term not found</div>;
  }

  // Get formatted artisan name
  const categoryData = allowedArtisans[category];
  const formattedCategoryName =
    categoryData?.formattedName ||
    category.charAt(0).toUpperCase() + category.slice(1).replace(/-/g, " ");

  const currentLetter = letter.toUpperCase();

  return (
    <>
      <Helmet>
        <title>
          {`${term.term} - ${formattedCategoryName} Glossary | LaborHack`}
        </title>
        <meta
          name="description"
          content={`${term.term}: ${
            term.definition
          } - Professional ${formattedCategoryName.toLowerCase()} terminology and definitions.`}
        />
      </Helmet>

      <Navbar colorScheme="prussianBlue" />

      <div className="bg-[#FEFEFE] min-h-screen py-10">
        <section className="w-full max-w-screen-2xl mx-auto px-4 md:px-16 py-8">
          {/* Breadcrumb */}
          <Breadcrumb className="mb-6">
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <RouterLink to="/glossary">Glossary</RouterLink>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <RouterLink to={`/glossary/${category}/a`}>
                    {formattedCategoryName}
                  </RouterLink>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <RouterLink
                    to={`/glossary/${category}/${letter.toLowerCase()}`}
                  >
                    {currentLetter}
                  </RouterLink>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>{term.term}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          {/* Promotional Text */}
          {!categoriesError && (
            <PromotionalText
              text={glossaryData[category].promotionalText}
              category={categoryId}
              loading={categoriesLoading}
            />
          )}

          {/* Term Detail Card */}
          <div className="bg-white border border-[#E5E7EB] p-6 md:p-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <h1 className="text-[#153243] text-2xl md:text-3xl font-medium font-[AeonikPro]">
                {term.term}
              </h1>
              <span className="inline-flex items-center px-3 py-1 w-fit rounded-full text-sm bg-[#FFF1EF] text-[#ED6E5C]">
                {formattedCategoryName}
              </span>
            </div>

            <div className="mt-6">
              <h2 className="text-[#475467] text-base font-semibold mb-2">
                Definition
              </h2>
              <p className="text-[#0B1922] text-base md:text-lg leading-7 md:leading-8 max-w-4xl">
                {term.definition}
              </p>
            </div>
          </div>

          {/* Navigation Links */}
          <div className="flex flex-row items-center justify-between gap-3 pt-4">
            <RouterLink
              to={`/glossary/${category}/${letter.toLowerCase()}`}
              className="text-[#ED6E5C] hover:text-[#D85D4A] text-sm md:text-base font-medium hover:underline flex items-center gap-2"
            >
              <FaArrowLeft className="w-3 h-3 md:w-3.5 md:h-3.5" /> Back to{" "}
              {currentLetter} terms
            </RouterLink>

            <RouterLink
              to="/glossary"
              className="text-[#646D72] hover:text-[#464646] text-sm md:text-base hover:underline"
            >
              All glossaries
            </RouterLink>
          </div>
        </section>
      </div>

      <Footer />
    </>
  );
};
